import type { FC } from 'react';

import Avatar from '@/common-components/Avatar';
import Pill from '@/common-components/Pill';
import { cn } from '@/utils/cn.util';
import { timeAgo } from '@/utils/date.util';
import type { AdminReportListItem } from '@/types/moderation.types';

import { REPORT_TARGET_PILL } from '../constants';
import ModerationStatusPill from './ModerationStatusPill';

interface ReportsListProps {
  items: AdminReportListItem[];
  mutatingId: string | null;
  onView: (item: AdminReportListItem) => void;
  onOpenResolve: (item: AdminReportListItem) => void;
}

const actionBtn =
  'cursor-pointer whitespace-nowrap rounded-lg px-3.5 py-[7px] text-[12px] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50';

/** Reports-queue rows in the same .mod-item visual language as the shoutt queue. */
const ReportsList: FC<ReportsListProps> = ({ items, mutatingId, onView, onOpenResolve }) => (
  <div className="flex flex-col gap-2.5">
    {items.map((item) => {
      const mutating = mutatingId === item.id;
      const targetPill = REPORT_TARGET_PILL[item.targetType];

      return (
        <div
          key={item.id}
          className={cn(
            'flex items-start gap-3.5 rounded-xl border border-[var(--color-divider)] bg-white p-4 transition-all hover:shadow-[var(--shadow-card)]',
            item.status === 'open' && 'border-l-[3px] border-l-[var(--color-danger)]',
          )}
        >
          <Avatar name={item.reporter.name} src={item.reporter.avatarUrl} size="lg" />

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-[13.5px] font-semibold text-[var(--color-ink)]">
                {item.reporter.name}
              </span>
              {item.origin === 'ai' ? (
                <Pill tone="pink" className="text-[10px]">🤖 AI</Pill>
              ) : null}
              <span className="text-[12px] text-[var(--color-ink-soft)]">reported</span>
              <Pill tone={targetPill.tone} className="text-[10px]">
                {targetPill.label}
              </Pill>
              <ModerationStatusPill kind="reportStatus" value={item.status} className="text-[10px]" />
            </div>

            <p className="mb-2 text-[12.5px] leading-normal text-[var(--color-ink-muted)]">
              “{item.reason}”
            </p>

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="max-w-[360px] truncate rounded-md bg-[var(--color-surface-muted)] px-2 py-1 text-[11.5px] font-medium text-[var(--color-ink)]">
                {item.target.label}
              </span>
              {item.target.sublabel ? (
                <span className="text-[11px] text-[var(--color-ink-soft)]">
                  {item.target.sublabel}
                </span>
              ) : null}
              <span className="text-[11px] text-[var(--color-ink-soft)]">
                · {timeAgo(item.createdAt)}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-1.5">
            {item.status !== 'resolved' ? (
              <button
                type="button"
                disabled={mutating}
                onClick={() => onOpenResolve(item)}
                className={cn(
                  actionBtn,
                  'bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white',
                )}
              >
                {mutating ? 'Resolving…' : '✓ Resolve'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onView(item)}
              className={cn(
                actionBtn,
                'border border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)]',
              )}
            >
              Details
            </button>
          </div>
        </div>
      );
    })}
  </div>
);

export default ReportsList;
